import React from 'react';
import useMedicalProfile from '../../../hooks/api/useMedicalProfile';
import Stack from '../Stack';
import { LoadableAvatar, getFallbackFromName } from '../Avatar';
import urls from '../../../utils/urls';
import getName from '../../../utils/getName';
import SkeletonLoadingContainer from '../SkeletonLoadingContainer';
import Text from '../Text';
import getDateString from '../../../utils/getDateString';
import getAge from '../../../utils/getAge';
import MainContentContainer from '../MainContentContainer';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import MaxWidthRestrictedContent from '../MaxWidthRestrictedContent';
import { colors } from '../../../utils/theme';
import { StyleProp, ViewStyle } from 'react-native';
import AIChatButton from '../AIChatButton';
import { useIsInSideTabContentContainer } from '../TabView';

export type ProfileHeaderContainer = {
    profileId: string | null;
    children: React.ReactNode | React.ReactNode[];
    style?: StyleProp<ViewStyle>;
    bottomBorder?: boolean;
    omitChatButton?: boolean;
};

export type ProfileHeaderContainerProps = ProfileHeaderContainer;

const ProfileHeaderContainer: React.FC<ProfileHeaderContainerProps> = ({
    profileId,
    children,
    style,
    bottomBorder,
    omitChatButton,
}) => {
    const isMobile = useIsMobileLayout();
    const { isLoadingProfile, profile } = useMedicalProfile({
        medicalProfileId: profileId,
    });
    const isInSideTabContentContainer = useIsInSideTabContentContainer();

    if (isMobile || !profileId || isInSideTabContentContainer) return children;

    return (
        <Stack vertical fullWidth flex={1} style={style}>
            <MaxWidthRestrictedContent>
                <Stack horizontal fullWidth style={{ backgroundColor: colors.White }} testID="profile-header-container">
                    <MainContentContainer
                        style={{
                            paddingTop: isMobile ? 0 : undefined,
                            ...(bottomBorder
                                ? { borderBottomWidth: 1, borderBottomColor: colors.Dividers }
                                : {
                                      paddingBottom: 0,
                                  }),
                        }}
                    >
                        <Stack horizontal fullWidth justify="space-between" align="center">
                            <Stack horizontal justify="flex-start" align="center" space={4}>
                                <LoadableAvatar
                                    size="profile-header"
                                    avatarUrl={urls.api.medicalProfiles.avatar(profileId)}
                                    fallbackName={getFallbackFromName(profile ? getName(profile.contactDetails) : '')}
                                />
                                <Stack vertical space={1}>
                                    <SkeletonLoadingContainer isLoading={isLoadingProfile || !profile}>
                                        <Text variant="profile-header-text">
                                            {profile ? getName(profile.contactDetails) : 'FirstName LastName'}
                                        </Text>
                                    </SkeletonLoadingContainer>
                                    <SkeletonLoadingContainer isLoading={isLoadingProfile || !profile}>
                                        <Text variant="card-body">
                                            {profile?.contactDetails.dob
                                                ? `${getDateString(profile.contactDetails.dob, 'date')} (${getAge(
                                                      profile.contactDetails.dob!,
                                                  )})`
                                                : '01/01/2000 (20)'}
                                        </Text>
                                    </SkeletonLoadingContainer>
                                </Stack>
                            </Stack>
                            {omitChatButton ? null : (
                                <AIChatButton medicalProfileId={profileId} useNestedNavigation={false} />
                            )}
                        </Stack>
                    </MainContentContainer>
                </Stack>
            </MaxWidthRestrictedContent>
            <Stack vertical fullWidth flex={1}>
                {children}
            </Stack>
        </Stack>
    );
};

export default React.memo(ProfileHeaderContainer);
